import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevRt,
  RefreshCw, X, AlertTriangle, Calendar, EyeOff, Eye, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MOIS_FR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const JOURS_FR = ['D','L','M','M','J','V','S'];

function addDays(d, n) {
  const r = new Date(d); r.setDate(r.getDate() + n); return r;
}
function toISO(d) { return d.toISOString().split('T')[0]; }
function parseISO(s) {
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day);
}
function dayDiff(isoA, isoB) {
  return Math.round((parseISO(isoB) - parseISO(isoA)) / 86400000);
}

// ─── Constantes visuelles ─────────────────────────────────────────────────────

const LEFT_W   = 260; // px colonne info matériel
const ROW_H    = 38;  // px hauteur ligne matériel
const HEAD_H   = 48;  // px hauteur header jours (double ligne)
const TRACK_H  = 15;  // px hauteur d'une barre (contrat ou OR)

// minDayW : largeur minimale par jour (déclenchement du scroll horizontal)
const VIEW_CONFIGS = {
  week:    { label: 'Semaine',  days: 7,    minDayW: 42 },
  '2weeks': { label: '2 Sem.',  days: 14,   minDayW: 30 },
  month:   { label: 'Mois',     days: null, minDayW: 20 },
};

const HATCH_PANNE  = 'repeating-linear-gradient(-45deg,#DC2626,#DC2626 2px,#FEE2E2 2px,#FEE2E2 8px)';
const HATCH_URGENT = 'repeating-linear-gradient(-45deg,#EA580C,#EA580C 2px,#FFEDD5 2px,#FFEDD5 8px)';

const JOURS_LABELS = ['LU', 'MA', 'ME', 'JE', 'VE', 'SA', 'DI'];

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function fmtMontant(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(n) + ' €';
}

// ─── Tokenized Search Bar ─────────────────────────────────────────────────────

// Normalise une chaîne : minuscules + suppression des accents/diacritiques
function norm(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function TokenSearch({ tokens, setTokens }) {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  const addToken = (val) => {
    const v = norm(val.trim());
    if (v && !tokens.includes(v)) setTokens(t => [...t, v]);
    setInput('');
  };

  const handleKey = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && input.trim()) {
      e.preventDefault(); addToken(input);
    }
    if (e.key === 'Backspace' && !input && tokens.length > 0)
      setTokens(t => t.slice(0, -1));
  };

  return (
    <div
      data-testid="planning-search"
      onClick={() => inputRef.current?.focus()}
      className="flex flex-wrap items-center gap-1 px-3 py-1.5 rounded-lg border bg-white cursor-text"
      style={{ borderColor: 'var(--border)', minWidth: 260, minHeight: 36 }}
    >
      <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
      {tokens.map(tok => (
        <span key={tok}
          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
          style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
          {tok}
          <button onClick={e => { e.stopPropagation(); setTokens(t => t.filter(x => x !== tok)); }}>
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKey}
        placeholder={tokens.length ? '' : 'Catégorie, gamme, marque, parc, client…'}
        className="outline-none text-sm flex-1 min-w-24"
        style={{ color: 'var(--text-secondary)', background: 'transparent' }}
        data-testid="planning-search-input"
      />
    </div>
  );
}

// ─── Popover détail contrat ───────────────────────────────────────────────────

function ContratPopover({ popover, onClose, onNavigate }) {
  const { ev, rect, detail } = popover;
  const POPOVER_W = 300;

  // Position above the bar, clamped to viewport
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - POPOVER_W - 8);
  const bottomOffset = window.innerHeight - rect.top + 8;

  // Union des jours comptés à travers toutes les lignes matériels
  // V1 : detail.lignes_materiels · V2 : detail.lignes (même champ jours_exclus)
  const isV2 = ev.source === 'v2';
  const lignes = detail?.lignes_materiels || detail?.lignes || [];
  const joursComptes = JOURS_LABELS.map((_, i) =>
    lignes.length === 0 ? true : lignes.some(l => !(l.jours_exclus || [5, 6]).includes(i))
  );

  const totalSuspensions = (Array.isArray(detail?.suspensions) ? detail.suspensions : [])
    .reduce((s, sp) => s + (sp.nb_jours || 0), 0);

  return (
    <>
      {/* Overlay fermeture clic extérieur */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={onClose} />

      {/* Popover */}
      <div
        data-testid="planning-contrat-popover"
        onClick={e => { e.stopPropagation(); onNavigate(ev.contrat_id); }}
        style={{
          position: 'fixed',
          bottom: bottomOffset,
          left,
          width: POPOVER_W,
          zIndex: 1000,
          background: 'white',
          borderRadius: 10,
          border: '1px solid #CBD5E1',
          boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        {/* Header bleu foncé */}
        <div style={{ padding: '10px 12px 8px', background: '#1E3A5F' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.83rem', fontWeight: 700, color: 'white' }}>
              {ev.numero}
            </span>
            <button
              data-testid="planning-popover-close"
              onClick={e => { e.stopPropagation(); onClose(); }}
              style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
            >
              <X size={13} />
            </button>
          </div>
          <div style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.85)', marginTop: 2, fontWeight: 500 }}>
            {ev.client_nom || '—'}
          </div>
          <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.55)', marginTop: 1 }}>
            {fmtDate(ev.date_debut)} → {fmtDate(ev.date_fin)}
          </div>
        </div>

        {/* Corps */}
        {!detail || detail.loading ? (
          <div style={{ padding: 16, display: 'flex', justifyContent: 'center' }}>
            <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: '#2563EB', borderTopColor: 'transparent' }} />
          </div>
        ) : detail.error ? (
          <div style={{ padding: 12, fontSize: '0.75rem', color: '#DC2626' }}>
            Erreur lors du chargement
          </div>
        ) : (
          <div style={{ padding: '10px 12px' }}>

            {/* Matériels */}
            {lignes.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#6B7280',
                  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  Matériels ({lignes.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {lignes.map((l, i) => (
                    <div key={l.ligne_id || i}
                      style={{ fontSize: '0.73rem', color: '#1E293B' }}>
                      {isV2
                        ? (<>
                            {[l.categorie_nom, l.gamme_nom].filter(Boolean).join(' ') || '—'}
                            {l.quantite > 1 && (
                              <span style={{ color: '#94A3B8', marginLeft: 4 }}>×{l.quantite}</span>
                            )}
                          </>)
                        : (<>
                            {[l.categorie_nom, l.gamme_nom, l.materiel_marque, l.materiel_modele].filter(Boolean).join(' ') || '—'}
                            {l.materiel_parc && (
                              <span style={{ color: '#94A3B8', marginLeft: 4 }}>N°{l.materiel_parc}</span>
                            )}
                          </>)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ height: 1, background: '#F1F5F9', margin: '7px 0' }} />

            {/* Jours comptés */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
              <span style={{ fontSize: '0.62rem', fontWeight: 600, color: '#6B7280',
                textTransform: 'uppercase', flexShrink: 0 }}>
                Jours comptés
              </span>
              <div style={{ display: 'flex', gap: 2 }}>
                {JOURS_LABELS.map((lbl, i) => (
                  <span key={lbl} style={{
                    fontSize: '0.6rem', fontWeight: 700,
                    padding: '2px 4px', borderRadius: 3,
                    background: joursComptes[i] ? '#DBEAFE' : '#F3F4F6',
                    color: joursComptes[i] ? '#1D4ED8' : '#CBD5E1',
                  }}>
                    {lbl}
                  </span>
                ))}
              </div>
            </div>

            {/* Suspensions */}
            <div style={{ fontSize: '0.72rem', color: '#64748B', marginBottom: 5, display: 'flex', gap: 4 }}>
              <span>Suspensions :</span>
              <span style={{ fontWeight: 700, color: totalSuspensions > 0 ? '#DC2626' : '#1E293B' }}>
                {totalSuspensions > 0 ? `${totalSuspensions} j` : 'aucune'}
              </span>
            </div>

            <div style={{ height: 1, background: '#F1F5F9', margin: '7px 0' }} />

            {/* Montant HT */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '0.62rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase' }}>
                Montant HT
              </span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1D4ED8' }}>
                {fmtMontant(detail.total_ht)}
              </span>
            </div>

            {/* Hint */}
            <div style={{ marginTop: 8, fontSize: '0.62rem', color: '#94A3B8', textAlign: 'right' }}>
              Cliquer pour ouvrir le contrat →
            </div>
          </div>
        )}
      </div>
    </>
  );
}


// ─── Bar components ───────────────────────────────────────────────────────────

const CONTRAT_BAR_COLORS = {
  confirme:    '#1D4ED8', // bleu — Réservé
  signe:       '#A16207', // jaune/ambre — Confirmé
  en_cours:    '#C2410C', // orange — Enlevé
  retour:      '#15803D', // vert — Retourné
  reservation: '#7C3AED', // violet — Réservation manuelle
};

function ContratBar({ ev, periodStart, nbDays, dayW, onBarClick }) {
  const startIdx = Math.max(0, dayDiff(periodStart, ev.date_debut));
  const endIdx   = Math.min(nbDays - 1, dayDiff(periodStart, ev.date_fin));
  if (startIdx > nbDays - 1 || endIdx < 0) return null;
  const left  = startIdx * dayW + 2;
  const width = Math.max(dayW - 4, (endIdx - startIdx + 1) * dayW - 4);
  const bg    = CONTRAT_BAR_COLORS[ev.statut] || '#2563EB';

  return (
    <div
      data-testid={`planning-contrat-${ev.contrat_id}`}
      onClick={e => { e.stopPropagation(); onBarClick(ev, e.currentTarget.getBoundingClientRect()); }}
      title={`${ev.statut ? `[${ev.statut}] ` : ''}${ev.numero} — ${ev.client_nom}`}
      style={{
        position: 'absolute', left, width,
        top: 3, height: TRACK_H,
        background: bg,
        borderRadius: 4,
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex', alignItems: 'center',
      }}
    >
      <span style={{ fontSize: 10, color: 'white', padding: '0 5px', whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {ev.client_nom || ev.numero}
      </span>
    </div>
  );
}

function ORBar({ ev, periodStart, nbDays, dayW, periodEnd, navigate }) {
  const dateDebut = ev.date_debut || periodStart;
  const dateFin   = ev.date_fin   || periodEnd;
  const startIdx  = Math.max(0, dayDiff(periodStart, dateDebut));
  const endIdx    = Math.min(nbDays - 1, dayDiff(periodStart, dateFin));
  if (startIdx > nbDays - 1 || endIdx < 0) return null;
  const left  = startIdx * dayW + 2;
  const width = Math.max(dayW - 4, (endIdx - startIdx + 1) * dayW - 4);
  const hatch = ev.type_or === 'urgent' ? HATCH_URGENT : HATCH_PANNE;

  return (
    <div
      data-testid={`planning-or-${ev.or_id}`}
      onClick={e => { e.stopPropagation(); navigate(`/atelier?ordre_id=${ev.or_id}`); }}
      title={`OR ${ev.type_or === 'urgent' ? 'Urgent' : 'Panne'} — ${ev.titre || ev.numero}`}
      style={{
        position: 'absolute', left, width,
        top: ROW_H - TRACK_H - 3, height: TRACK_H,
        background: hatch,
        borderRadius: 3,
        cursor: 'pointer',
        border: `1px solid ${ev.type_or === 'urgent' ? '#EA580C' : '#DC2626'}`,
      }}
    />
  );
}

// ─── Ligne Matériel ───────────────────────────────────────────────────────────

function MaterielRow({ mat, gammeId, gammeNom, days, dayW, periodStart, periodEnd, nbDays, navigate, isOdd, onBarClick }) {
  const contrats = mat.events.filter(e => e.type === 'contrat');
  const orEvents = mat.events.filter(e => e.type === 'or');

  return (
    <tr
      data-testid={`planning-mat-${mat.materiel_id}`}
      style={{ borderBottom: '1px solid var(--border-light)' }}
    >
      {/* Info matériel */}
      <td style={{
        position: 'sticky', left: 0, zIndex: 10,
        width: LEFT_W, minWidth: LEFT_W, maxWidth: LEFT_W,
        height: ROW_H, padding: '0 12px',
        background: isOdd ? '#FAFAFA' : 'white',
        borderRight: '1px solid var(--border)',
        verticalAlign: 'middle',
      }}>
        <div className="flex items-center gap-2">
          {(mat.has_or_bloquant || mat.vgp_overdue) && (
            <span title={mat.has_or_bloquant ? 'OR panne/urgent actif' : 'VGP expirée'}
              style={{ color: '#DC2626', flexShrink: 0 }}>
              <AlertTriangle size={11} />
            </span>
          )}
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {[mat.marque, mat.modele_nom].filter(Boolean).join(' ') || '—'}
            </div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {mat.numero_parc ? `N°${mat.numero_parc}` : mat.immatriculation || mat.numero_serie || ''}
            </div>
          </div>
        </div>
      </td>

      {/* Zone Gantt — clic sur cellule vide → nouveau contrat */}
      <td
        colSpan={nbDays}
        onClick={e => {
          // Calcul du jour cliqué depuis la position relative dans le td
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const dayIdx = Math.max(0, Math.min(nbDays - 1, Math.floor(x / dayW)));
          const clickedIso = days[dayIdx]?.iso;
          if (!clickedIso) return;
          // Construire l'URL vers la page Contrats avec pré-remplissage
          const params = new URLSearchParams({ new: '1', date_debut: clickedIso });
          if (mat.materiel_id) params.set('materiel_id', mat.materiel_id);
          if (gammeId && gammeId !== '_no_gamme') params.set('gamme_id', gammeId);
          if (gammeNom) params.set('gamme_nom', gammeNom);
          if (mat.marque) params.set('mat_label', [mat.marque, mat.modele_nom].filter(Boolean).join(' '));
          navigate(`/locations-v2?${params.toString()}`);
        }}
        style={{
          position: 'relative', padding: 0, height: ROW_H,
          background: isOdd ? '#FAFAFA' : 'white',
          cursor: 'crosshair',
        }}
      >
        {/* Grid des jours (fonds weekends) */}
        {days.map((d, i) => (
          <div key={d.iso} style={{
            position: 'absolute', left: i * dayW, top: 0,
            width: dayW, height: ROW_H,
            background: d.isWeekend ? 'rgba(0,0,0,0.03)' : 'transparent',
            borderRight: '1px solid #F0F0F0',
            pointerEvents: 'none',
          }} />
        ))}

        {/* Indicateur VGP : tint rouge très léger si expiré */}
        {mat.vgp_overdue && (
          <div style={{
            position: 'absolute', left: 0, top: 0,
            width: nbDays * dayW, height: ROW_H,
            background: 'rgba(220,38,38,0.04)',
            pointerEvents: 'none',
          }} />
        )}

        {/* Barres contrats */}
        {contrats.map(ev => (
          <ContratBar key={ev.contrat_id} ev={ev}
            periodStart={periodStart} nbDays={nbDays} dayW={dayW} onBarClick={onBarClick} />
        ))}

        {/* Barres OR */}
        {orEvents.map(ev => (
          <ORBar key={ev.or_id} ev={ev}
            periodStart={periodStart} periodEnd={periodEnd}
            nbDays={nbDays} dayW={dayW} navigate={navigate} />
        ))}
      </td>
    </tr>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function Planning() {
  const navigate = useNavigate();
  const { activeAgences } = useSociete();

  const [viewMode, setViewMode]       = useState('2weeks');
  const [anchorDate, setAnchorDate]   = useState(new Date());
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [tokens, setTokens]           = useState([]);
  const [hideOrBloquant, setHide] = useState(false);
  const [collapsedCats, setCollCats]  = useState(new Set());
  const [collapsedGammes, setCollG]   = useState(new Set());
  // Filtres par statut de contrat (Set vide = tout afficher)
  const [statutFilters, setStatutFilters] = useState(new Set());

  const toggleStatutFilter = useCallback((statut) => {
    setStatutFilters(prev => {
      const next = new Set(prev);
      if (next.has(statut)) next.delete(statut);
      else next.add(statut);
      return next;
    });
  }, []);

  // ── Mesure du conteneur pour dayW adaptatif ────────────────────────────────
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Popover détail contrat ──────────────────────────────────────────────────
  const [popover, setPopover] = useState(null);  // { ev, rect, detail }
  const popoverCache = useRef({});

  const handleBarClick = useCallback(async (ev, rect) => {
    // Ouvrir le popover immédiatement avec état chargement
    setPopover({ ev, rect, detail: { loading: true } });

    // Si déjà en cache, utiliser directement (clé namespacée par source V1/V2)
    const cacheKey = `${ev.source || 'v1'}:${ev.contrat_id}`;
    if (popoverCache.current[cacheKey]) {
      setPopover({ ev, rect, detail: popoverCache.current[cacheKey] });
      return;
    }

    try {
      const res = ev.source === 'v2'
        ? await api.getContratV2(ev.contrat_id)
        : await api.getContrat(ev.contrat_id);
      const data = res.data;
      popoverCache.current[cacheKey] = data;
      setPopover(prev => prev ? { ...prev, detail: data } : null);
    } catch (err) {
      console.error('[Planning] Popover contrat fetch error:', err);
      setPopover(prev => prev ? { ...prev, detail: { error: true } } : null);
    }
  }, []);

  // Fermeture sur Echap
  useEffect(() => {
    if (!popover) return;
    const handler = e => { if (e.key === 'Escape') setPopover(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [popover]);

  // ── Période ────────────────────────────────────────────────────────────────
  const { periodStart, periodEnd, days } = useMemo(() => {
    const anchor = new Date(anchorDate);
    anchor.setHours(0, 0, 0, 0);
    let d1, d2;

    if (viewMode === 'week' || viewMode === '2weeks') {
      const dow  = anchor.getDay();
      const diff = dow === 0 ? -6 : 1 - dow; // aligne lundi
      d1 = addDays(anchor, diff);
      d2 = addDays(d1, viewMode === 'week' ? 6 : 13);
    } else {
      d1 = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      d2 = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    }

    const daysList = [];
    let cur = new Date(d1);
    while (cur <= d2) {
      const dow = cur.getDay();
      daysList.push({
        iso:       toISO(cur),
        num:       cur.getDate(),
        dowLbl:    JOURS_FR[dow],
        moisLbl:   MOIS_FR[cur.getMonth()],
        isWeekend: dow === 0 || dow === 6,
        isToday:   toISO(cur) === toISO(new Date()),
      });
      cur = addDays(cur, 1);
    }

    return { periodStart: toISO(d1), periodEnd: toISO(d2), days: daysList };
  }, [viewMode, anchorDate]);

  const nbDays = days.length;

  // dayW adaptatif : remplit la largeur disponible du conteneur
  const dayW = useMemo(() => {
    const minDW = VIEW_CONFIGS[viewMode]?.minDayW ?? 28;
    if (containerWidth > LEFT_W + minDW * nbDays) {
      return Math.floor((containerWidth - LEFT_W) / nbDays);
    }
    return minDW;
  }, [viewMode, nbDays, containerWidth]);

  // ── Chargement ─────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getPlanningDisponibilite({ date_debut: periodStart, date_fin: periodEnd });
      setData(res.data);
    } catch {
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setLoading(false);
    }
  }, [periodStart, periodEnd, activeAgences]);

  useEffect(() => { load(); }, [load]);

  // ── Navigation période ─────────────────────────────────────────────────────
  const navigate_period = (dir) => {
    if (viewMode === 'week')    setAnchorDate(d => addDays(d, dir * 7));
    else if (viewMode === '2weeks') setAnchorDate(d => addDays(d, dir * 14));
    else                        setAnchorDate(d => {
      const n = new Date(d); n.setMonth(n.getMonth() + dir); return n;
    });
  };

  // ── Filtrage (tokens + toggle OR + statut) ───────────────────────────────
  const filteredCats = useMemo(() => {
    if (!data?.categories) return [];
    return data.categories.map(cat => {
      const filteredGammes = cat.gammes.map(gamme => {
        let mats = gamme.materiels;
        if (hideOrBloquant) mats = mats.filter(m => !m.has_or_bloquant);
        if (tokens.length > 0) {
          mats = mats.filter(mat => {
            const clientNoms = mat.events.filter(e => e.type === 'contrat').map(e => e.client_nom);
            const fields = [
              cat.categorie_nom, gamme.gamme_nom,
              mat.marque, mat.modele_nom, mat.numero_parc,
              mat.numero_serie, mat.immatriculation,
              ...clientNoms,
            ].map(f => norm(f));
            return tokens.every(tok => fields.some(f => f.includes(tok)));
          });
        }
        // Filtre statut : une ligne est visible si elle a un événement correspondant
        // (contrat OU OR selon les filtres actifs)
        if (statutFilters.size > 0) {
          const contractFilters = [...statutFilters].filter(f => !f.startsWith('or_'));
          const orFilters       = [...statutFilters].filter(f =>  f.startsWith('or_'));
          mats = mats.filter(mat => {
            const matchesContract = contractFilters.length > 0 &&
              mat.events.some(e => e.type === 'contrat' && contractFilters.includes(e.statut));
            const matchesOR = orFilters.length > 0 &&
              mat.events.some(e => e.type === 'or' && orFilters.includes(`or_${e.type_or || 'panne'}`));
            return matchesContract || matchesOR;
          });
        }
        return { ...gamme, materiels: mats };
      }).filter(g => g.materiels.length > 0);
      return { ...cat, gammes: filteredGammes };
    }).filter(c => c.gammes.length > 0);
  }, [data, tokens, hideOrBloquant, statutFilters]);

  const totalW  = LEFT_W + nbDays * dayW;
  const countOrBloquants = useMemo(() =>
    (data?.categories ?? []).flatMap(c => c.gammes.flatMap(g => g.materiels)).filter(m => m.has_or_bloquant).length
  , [data]);

  const totalMats = useMemo(() =>
    filteredCats.flatMap(c => c.gammes.flatMap(g => g.materiels)).length
  , [filteredCats]);

  // ── Label période ──────────────────────────────────────────────────────────
  const periodeLabel = useMemo(() => {
    const d1 = parseISO(periodStart), d2 = parseISO(periodEnd);
    if (d1.getMonth() === d2.getMonth())
      return `${d1.getDate()} – ${d2.getDate()} ${MOIS_FR[d1.getMonth()]} ${d1.getFullYear()}`;
    return `${d1.getDate()} ${MOIS_FR[d1.getMonth()]} – ${d2.getDate()} ${MOIS_FR[d2.getMonth()]} ${d2.getFullYear()}`;
  }, [periodStart, periodEnd]);

  // ── Matériel row counter for alternating bg ────────────────────────────────
  let rowIdx = 0;

  return (
    <div className="h-full flex flex-col" style={{ background: 'var(--bg-subtle)' }}>

      {/* ── Header ── */}
      <div className="px-6 py-4 flex-shrink-0"
        style={{ background: 'white', borderBottom: '1px solid var(--border)' }}>

        {/* Titre + card + refresh */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-secondary)' }}>Planning</h1>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Disponibilité du parc LCD — contrats, OR panne/urgent, VGP
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Card matériels + toggle fonctionnels */}
            <div
              data-testid="planning-or-card"
              onClick={() => setHide(v => !v)}
              className="bg-white rounded-xl border text-left"
              style={{ cursor: 'pointer', userSelect: 'none', width: 252, padding: '10px 14px' }}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-2xl font-bold" style={{ color: 'var(--text)',  }}>{totalMats}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {hideOrBloquant ? 'Fonctionnels uniquement' : 'Tous les matériels'}
                  </p>
                </div>
                <div style={{
                  width: 40, height: 22, borderRadius: 11, flexShrink: 0,
                  background: hideOrBloquant ? 'var(--primary-solid)' : '#E2E8F0',
                  transition: 'background 0.25s', position: 'relative',
                }}>
                  <div style={{
                    position: 'absolute', top: 3,
                    left: hideOrBloquant ? 19 : 3,
                    width: 16, height: 16, borderRadius: '50%',
                    background: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                    transition: 'left 0.25s',
                  }} />
                </div>
              </div>
            </div>

            <button onClick={load} disabled={loading}
              data-testid="planning-refresh"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium hover:opacity-80"
              style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Navigation + sélecteur vue + recherche + légende */}
        <div className="flex flex-wrap items-center gap-3">

          {/* Sélecteur mode */}
          <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
            {Object.entries(VIEW_CONFIGS).map(([key, cfg]) => (
              <button key={key}
                data-testid={`planning-view-${key}`}
                onClick={() => setViewMode(key)}
                className="px-3 py-1.5 text-xs font-semibold transition-colors"
                style={{
                  background: viewMode === key ? 'var(--primary-solid)' : 'white',
                  color:      viewMode === key ? 'white' : 'var(--text-muted)',
                }}>
                {cfg.label}
              </button>
            ))}
          </div>

          {/* Nav période */}
          <div className="flex items-center gap-1">
            <button onClick={() => navigate_period(-1)} data-testid="planning-prev"
              className="p-1.5 rounded-lg border hover:bg-gray-50"
              style={{ borderColor: 'var(--border)' }}>
              <ChevronLeft size={15} style={{ color: 'var(--text-muted)' }} />
            </button>
            <span className="px-3 text-sm font-semibold" style={{ color: 'var(--text)', minWidth: 180, textAlign: 'center' }}>
              {periodeLabel}
            </span>
            <button onClick={() => navigate_period(1)} data-testid="planning-next"
              className="p-1.5 rounded-lg border hover:bg-gray-50"
              style={{ borderColor: 'var(--border)' }}>
              <ChevronRight size={15} style={{ color: 'var(--text-muted)' }} />
            </button>
            <button onClick={() => setAnchorDate(new Date())} data-testid="planning-today"
              className="px-3 py-1.5 rounded-lg border text-xs font-semibold hover:bg-gray-50"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              Aujourd'hui
            </button>
          </div>

          {/* Recherche tokenisée */}
          <TokenSearch tokens={tokens} setTokens={setTokens} />

          {/* Légende — alignée à droite, boutons filtrants pour les statuts contrat */}
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            {[
              { bg: CONTRAT_BAR_COLORS.confirme,    label: 'Réservé',          statut: 'confirme' },
              { bg: CONTRAT_BAR_COLORS.signe,       label: 'Confirmé',         statut: 'signe' },
              { bg: CONTRAT_BAR_COLORS.en_cours,    label: 'Enlevé',           statut: 'en_cours' },
              { bg: CONTRAT_BAR_COLORS.retour,      label: 'Retourné',         statut: 'retour' },
              { bg: CONTRAT_BAR_COLORS.reservation, label: 'Réservation',      statut: 'reservation' },
            ].map(item => {
              const isActive = statutFilters.has(item.statut);
              return (
                <button
                  key={item.label}
                  data-testid={`planning-filter-${item.statut}`}
                  onClick={() => toggleStatutFilter(item.statut)}
                  title={isActive ? `Retirer le filtre "${item.label}"` : `Filtrer par "${item.label}"`}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-all text-xs font-medium"
                  style={{
                    borderColor:      isActive ? item.bg : 'var(--border)',
                    background:       isActive ? item.bg + '22' : 'white',
                    color:            isActive ? item.bg : 'var(--text-muted)',
                    boxShadow:        isActive ? `0 0 0 1.5px ${item.bg}` : 'none',
                    fontWeight:       isActive ? 700 : 500,
                  }}>
                  <span style={{
                    display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                    background: item.bg, flexShrink: 0,
                    opacity: isActive ? 1 : 0.7,
                  }} />
                  {item.label}
                  {isActive && <span style={{ fontSize: '0.6rem', marginLeft: 1, opacity: 0.8 }}>✕</span>}
                </button>
              );
            })}
            {/* OR Panne / Urgent : filtrants aussi */}
            {[
              { bg: HATCH_PANNE,  label: 'OR Panne',  key: 'or_panne',  border: '#DC2626', color: '#DC2626' },
              { bg: HATCH_URGENT, label: 'OR Urgent',  key: 'or_urgent', border: '#EA580C', color: '#EA580C' },
            ].map(item => {
              const isActive = statutFilters.has(item.key);
              return (
                <button
                  key={item.label}
                  data-testid={`planning-filter-${item.key}`}
                  onClick={() => toggleStatutFilter(item.key)}
                  title={isActive ? `Retirer le filtre "${item.label}"` : `Filtrer par "${item.label}"`}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-all text-xs font-medium"
                  style={{
                    borderColor:  isActive ? item.border : 'var(--border)',
                    background:   isActive ? item.color + '18' : 'white',
                    color:        isActive ? item.color : 'var(--text-muted)',
                    boxShadow:    isActive ? `0 0 0 1.5px ${item.border}` : 'none',
                    fontWeight:   isActive ? 700 : 500,
                  }}>
                  <span style={{
                    display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                    background: item.bg, border: `1px solid ${item.border}`, flexShrink: 0,
                    opacity: isActive ? 1 : 0.7,
                  }} />
                  {item.label}
                  {isActive && <span style={{ fontSize: '0.6rem', marginLeft: 1, opacity: 0.8 }}>✕</span>}
                </button>
              );
            })}
            {/* Badge compteur quand filtre actif */}
            {statutFilters.size > 0 && (
              <button
                onClick={() => setStatutFilters(new Set())}
                data-testid="planning-filter-reset"
                className="px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors hover:bg-red-50"
                style={{ borderColor: '#DC2626', color: '#DC2626', background: 'white' }}
                title="Effacer tous les filtres statut">
                Effacer filtres
              </button>
            )}
          </div>

        </div>

        </div>

      {/* ── Tableau Gantt ── */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <div ref={containerRef} className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'white' }}>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
            </div>
          ) : filteredCats.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Calendar size={36} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Aucun matériel LCD{tokens.length ? ' pour cette recherche' : ''}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
              <table style={{
                borderCollapse: 'collapse', tableLayout: 'fixed',
                minWidth: totalW, width: totalW,
              }}>
                <colgroup>
                  <col style={{ width: LEFT_W }} />
                  {days.map(d => <col key={d.iso} style={{ width: dayW }} />)}
                </colgroup>

                {/* Header jours */}
                <thead style={{ position: 'sticky', top: 0, zIndex: 20 }}>
                  <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                    {/* Coin haut-gauche */}
                    <th style={{
                      position: 'sticky', left: 0, top: 0, zIndex: 30,
                      background: 'var(--bg-subtle)',
                      borderRight: '1px solid var(--border)',
                      height: HEAD_H, padding: '0 12px', textAlign: 'left',
                    }}>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)',
                        textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Matériel
                      </span>
                    </th>

                    {/* Colonnes jours */}
                    {days.map(d => (
                      <th key={d.iso} style={{
                        position: 'sticky', top: 0, zIndex: 19,
                        height: HEAD_H, padding: 0, textAlign: 'center',
                        background: d.isToday ? '#EFF6FF' : d.isWeekend ? '#F9FAFB' : 'var(--bg-subtle)',
                        borderRight: '1px solid #F0F0F0',
                        borderBottom: d.isToday ? `2px solid var(--primary-solid)` : undefined,
                      }}>
                        <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                          {d.dowLbl}
                        </div>
                        <div style={{
                          fontSize: '0.72rem', fontWeight: d.isToday ? 800 : 600,
                          color: d.isToday ? 'var(--primary-solid)' : d.isWeekend ? '#9CA3AF' : 'var(--text)',
                          lineHeight: 1.2,
                        }}>
                          {d.num}
                        </div>
                        {d.num === 1 && (
                          <div style={{ fontSize: '0.55rem', color: 'var(--text-muted)', marginTop: 1 }}>
                            {d.moisLbl}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filteredCats.map(cat => (
                    <React.Fragment key={cat.categorie_id}>

                      {/* ── En-tête catégorie ── */}
                      <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td
                          colSpan={nbDays + 1}
                          style={{
                            position: 'sticky', left: 0,
                            background: '#F1F5F9',
                            borderRight: '1px solid var(--border)',
                            padding: '0 12px', height: 42,
                          }}
                        >
                          <button
                            data-testid={`planning-cat-${cat.categorie_id}`}
                            onClick={() => setCollCats(s => {
                              const n = new Set(s);
                              n.has(cat.categorie_id) ? n.delete(cat.categorie_id) : n.add(cat.categorie_id);
                              return n;
                            })}
                            className="flex items-center gap-1.5 w-full text-left"
                          >
                            {collapsedCats.has(cat.categorie_id)
                              ? <ChevRt size={12} style={{ color: 'var(--text-muted)' }} />
                              : <ChevronDown size={12} style={{ color: 'var(--text-muted)' }} />}
                            <span style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text)',
                              textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {cat.categorie_nom}
                            </span>
                            <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                              ({cat.gammes.reduce((a, g) => a + g.materiels.length, 0)})
                            </span>
                          </button>
                        </td>
                      </tr>

                      {!collapsedCats.has(cat.categorie_id) && cat.gammes.map(gamme => (
                        <React.Fragment key={gamme.gamme_id}>

                          {/* ── En-tête gamme ── */}
                          <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                            <td
                              colSpan={nbDays + 1}
                              style={{
                                position: 'sticky', left: 0,
                                background: '#F8FAFC',
                                borderRight: '1px solid var(--border)',
                                padding: '0 12px 0 24px', height: 40,
                              }}
                            >
                              <button
                                data-testid={`planning-gamme-${gamme.gamme_id}`}
                                onClick={() => setCollG(s => {
                                  const n = new Set(s);
                                  n.has(gamme.gamme_id) ? n.delete(gamme.gamme_id) : n.add(gamme.gamme_id);
                                  return n;
                                })}
                                className="flex items-center gap-1.5 w-full text-left"
                              >
                                {collapsedGammes.has(gamme.gamme_id)
                                  ? <ChevRt size={11} style={{ color: 'var(--text-muted)' }} />
                                  : <ChevronDown size={11} style={{ color: 'var(--text-muted)' }} />}
                                <span style={{ fontSize: '0.79rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                                  {gamme.gamme_nom}
                                </span>
                                <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                                  ({gamme.materiels.length})
                                </span>
                              </button>
                            </td>
                          </tr>

                          {/* ── Lignes matériels ── */}
                          {!collapsedGammes.has(gamme.gamme_id) && gamme.materiels.map(mat => {
                            const isOdd = (rowIdx++ % 2 === 0);
                            return (
                              <MaterielRow key={mat.materiel_id}
                                mat={mat} days={days} dayW={dayW}
                                gammeId={gamme.gamme_id} gammeNom={gamme.gamme_nom}
                                periodStart={periodStart} periodEnd={periodEnd}
                                nbDays={nbDays} navigate={navigate} isOdd={isOdd}
                                onBarClick={handleBarClick}
                              />
                            );
                          })}

                        </React.Fragment>
                      ))}

                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>

      {/* Popover détail contrat */}
      {popover && (
        <ContratPopover
          popover={popover}
          onClose={() => setPopover(null)}
          onNavigate={id => {
            setPopover(null);
            navigate(popover.ev?.source === 'v2'
              ? `/location-v2/contrats?contrat_id=${id}`
              : `/contrats?contrat_id=${id}`);
          }}
        />
      )}
    </div>
  );
}
